package com.loyola.acessoescola.services.quintadb


import com.loyola.acessoescola.data.Error
import com.loyola.acessoescola.data.JsonInValues
import com.loyola.acessoescola.data.JsonOutValues
import com.loyola.acessoescola.data.RecordResponse
import com.loyola.acessoescola.data.RecordsResponse
import com.loyola.acessoescola.ui.readBytesFromURI
import com.loyola.acessoescola.ui.uuid
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.request.delete
import io.ktor.client.request.forms.formData
import io.ktor.client.request.forms.submitFormWithBinaryData
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.Headers
import io.ktor.http.HttpHeaders
import io.ktor.http.HttpMethod
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import io.ktor.serialization.kotlinx.json.json
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.serialization.json.Json

class AcessoEscolaAPI {
    private val httpClient = HttpClient {
        install(ContentNegotiation) {
            json(Json {
                prettyPrint = true
                isLenient = true
                ignoreUnknownKeys = true
            })
        }
    }

    fun getRecord(userIDToken: String, recordID: String): Flow<JsonOutValues> = flow {
        val response =
            httpClient.get("${EndPoints.RECORD.endPoint}?record_id=$recordID") {
                headers.append(HttpHeaders.Authorization, "Bearer $userIDToken")
            }

        try {
            val record: RecordResponse = response.body()
            emit(record.record)
        } catch (_: Exception) {
            val error: Error = response.body()

            if (error.errorMessage.contains("expired")) {
                throw ExpiredTokenException(ErrorMessages.EXPIRED_TOKEN.message)
            } else {
                throw UnauthorizedException(error.errorMessage)
            }
        }
    }

    fun getRecords(userIDToken: String): Flow<List<JsonOutValues>> = flow {
        val response =
            httpClient.get(EndPoints.RECORDS.endPoint) {
                headers.append(HttpHeaders.Authorization, "Bearer $userIDToken")
            }

        try {
            val records: RecordsResponse = response.body()
            emit(records.records)
        } catch (_: Exception) {
            val error: Error = response.body()

            if (error.errorMessage.contains("expired")) {
                throw ExpiredTokenException(ErrorMessages.EXPIRED_TOKEN.message)
            } else {
                throw UnauthorizedException(error.errorMessage)
            }
        }

    }

    private suspend fun uploadImage(
        imageUri: String,
        userIDToken: String,
        recordID: String
    ): String {
        val fileDataFieldName = "filedata"
        val filename = "FacialImage"
        val fileBytes = readBytesFromURI(imageUri)
            ?: throw InvalidImageURIException("Upload falhou: Não foi possível obter os dados do uri: $imageUri")

        val response = httpClient.submitFormWithBinaryData(
            url = EndPoints.UPLOAD_FILE.endPoint,
            formData = formData {
                append("recordID", recordID)
                append(
                    key = fileDataFieldName,
                    value = fileBytes,
                    headers = Headers.build {
                        append(HttpHeaders.ContentType, ContentType.Image.toString())
                        append(
                            HttpHeaders.ContentDisposition,
                            "form-data; name=\"$fileDataFieldName\"; filename=\"$filename\""
                        )
                    }
                )
            }
        ) {
            method = HttpMethod.Post
            header(HttpHeaders.Authorization, "Bearer $userIDToken")
        }


        when (response.status) {
            HttpStatusCode.Unauthorized -> {
                val error: Error = response.body()

                if (error.errorMessage.contains("expired")) {
                    throw ExpiredTokenException(ErrorMessages.EXPIRED_TOKEN.message)
                } else {
                    throw UnauthorizedException(error.errorMessage)
                }
            }

            HttpStatusCode.BadRequest -> {
                val error: Error = response.body()
                throw FaceDetectionException(error.errorMessage)
            }

            HttpStatusCode.OK -> {
                return response.bodyAsText()
            }

            else -> {
                val error: Error = response.body()

                throw UploadException("Upload falhou: ${error.detail}")
            }
        }
    }

    suspend fun createRecord(
        imageUri: String,
        userIDToken: String,
        record: JsonInValues,
        onSuccessCallback: () -> Unit = {}
    ) {
        val upload = uploadImage(
            imageUri = imageUri,
            userIDToken = userIDToken,
            recordID = record.jsonValues.recordID
        )
        if (!upload.contains("Uploaded")) {
            return
        }

        val response = httpClient.post(EndPoints.CREATE_RECORD.endPoint) {
            headers.append(HttpHeaders.Authorization, "Bearer $userIDToken")
            contentType(ContentType.Application.Json)
            setBody(record)
        }

        when (response.status) {
            HttpStatusCode.Unauthorized -> {
                val error: Error = response.body()

                if (error.errorMessage.contains("expired")) {
                    throw ExpiredTokenException(ErrorMessages.EXPIRED_TOKEN.message)
                } else {
                    throw UnauthorizedException(error.errorMessage)
                }
            }

            HttpStatusCode.OK -> {
                onSuccessCallback()
            }

            else -> {
                val error: Error = response.body()
                throw CreateRecordException("Não foi possível criar o registro: ${error.errorMessage}")
            }
        }
    }

    suspend fun updateRecord(
        imageUri: String?,
        userIDToken: String,
        record: JsonInValues,
        onSuccessCallback: () -> Unit = {}
    ) {

        if (imageUri != null) {
            deleteRecord(userIDToken = userIDToken, record = record)
            record.jsonValues = record.jsonValues.copy(
                recordID = uuid()
            )
            createRecord(
                imageUri = imageUri,
                userIDToken = userIDToken,
                record = record,
                onSuccessCallback = onSuccessCallback
            )
        } else {
            val response = httpClient.put(EndPoints.UPDATE_RECORD.endPoint) {
                headers.append(HttpHeaders.Authorization, "Bearer $userIDToken")
                contentType(ContentType.Application.Json)
                setBody(record)
            }

            when (response.status) {
                HttpStatusCode.Unauthorized -> {
                    val error: Error = response.body()

                    if (error.errorMessage.contains("expired")) {
                        throw ExpiredTokenException(ErrorMessages.EXPIRED_TOKEN.message)
                    } else {
                        throw UnauthorizedException(error.errorMessage)
                    }
                }

                HttpStatusCode.OK -> {
                    onSuccessCallback()
                }

                else -> {
                    val error: Error = response.body()
                    throw UpdateRecordException("Não foi Atualizar o registro: ${error.errorMessage}")
                }
            }
        }
    }

    suspend fun deleteRecord(
        userIDToken: String,
        record: JsonInValues,
        onSuccessCallback: () -> Unit = {}
    ) {
        val response = httpClient.delete(EndPoints.DELETE_RECORD.endPoint) {
            headers.append(HttpHeaders.Authorization, "Bearer $userIDToken")
            contentType(ContentType.Application.Json)
            setBody(record)
        }

        when (response.status) {
            HttpStatusCode.Unauthorized -> {
                val error: Error = response.body()

                if (error.errorMessage.contains("expired")) {
                    throw ExpiredTokenException(ErrorMessages.EXPIRED_TOKEN.message)
                } else {
                    throw UnauthorizedException(error.errorMessage)
                }
            }

            HttpStatusCode.OK -> {
                onSuccessCallback()
            }

            else -> {
                val error: Error = response.body()
                throw DeleteRecordException("Não foi possível deletar o registro: ${error.detail}")
            }
        }
    }
}